"use client";

/**
 * Quality and depth, as rates rather than totals.
 *
 * THE ARGUMENT. "We read a lot of the web" is a claim anyone can make and nobody can check. "One in
 * five pages we audit gets rejected" is a claim about DISCIPLINE, and it is the one that matters
 * when the alternative on the market is a bought feed nobody filtered. Paired with how deep we go
 * into each domain, it says quality and reach at once — which is the pairing this section is for.
 *
 * NO TOTALS, DELIBERATELY. The upstream endpoints return absolute counters and this component
 * converts every one of them into a rate or a ratio before rendering. The index is still being
 * built; a running total invites the only comparison we lose and freezes into every cached copy and
 * AI summary of the page. A percentage is true at any size and gets no less impressive as we grow.
 * If you add a stat here, add a rate — never a count.
 *
 * FAIL QUIET. If either endpoint is unreachable the block renders dashes rather than a fallback
 * literal. A hardcoded "21%" would be a competitive claim this component could not stand behind.
 */

import { useEffect, useState } from "react";
import { ShieldCheck, Layers3, ScanSearch, Percent } from "lucide-react";
import { fmtCount, fmtShare as pct } from "@/lib/format-stat";
import { StatTileGrid } from "@/components/marketing/stat-tile";

const QUALITY_URL = "/api/index/quality";
const STATS_URL = "/api/index/stats";

type Quality = {
  audited?: number;
  confirmed_junk?: number;
  llm_judged?: number;
  llm_junk?: number;
  ambiguous?: number;
};
type Stats = {
  database?: { total_domains?: number; total_indexed_pages?: number };
};


export function IndexQualityLive(
  { initial }: { initial?: { quality?: Quality | null; stats?: Stats | null } | null },
) {
  /* Seeded by the server so the figure is in the HTML on first paint. Before this the value
     arrived only after hydration, so every visitor saw a dash first and anything that does not
     run JavaScript — crawlers, link previews — never saw it at all. The client fetch below is
     kept: it refreshes a page that may have been cached for minutes. */
  const [q, setQ] = useState<Quality | null>(initial?.quality ?? null);
  const [s, setS] = useState<Stats | null>(initial?.stats ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let on = true;
    Promise.all([
      fetch(QUALITY_URL, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)),
      fetch(STATS_URL, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([qq, ss]) => {
        if (!on) return;
        if (!qq && !ss) { setFailed(true); return; }
        setQ(qq);
        setS(ss);
      })
      .catch(() => { if (on) setFailed(true); });
    return () => { on = false; };
  }, []);

  const domains = s?.database?.total_domains;
  const pages = s?.database?.total_indexed_pages;
  // Pages per domain is the depth claim: a crawler that takes the homepage and moves on cannot
  // answer a question buried three levels into a site. Rounded hard — "358" and "357.4" say the
  // same thing and one of them looks like it was measured with instruments we do not have.
  const perDomain =
    domains && pages && domains > 0 ? fmtCount(Math.round(pages / domains)) : "—";

  const stats = [
    {
      icon: Layers3,
      value: perDomain,
      label: "Pages per domain",
      sub: "we read a site through, not just its front page",
    },
    // LABELS ARE PRECISE ON PURPOSE. The operations dashboard shows a 76% and a ~21% that mean
    // something entirely different — 76% of pages successfully INDEXED, and ~21% SKIPPED as
    // unchanged or non-indexable, which is not the same as junk. These two are junk rates from the
    // quality guard. Anyone holding both screens side by side must be able to tell which is which,
    // so neither label may be shortened to a bare "76%" or "rejected".
    {
      icon: ShieldCheck,
      value: pct(q?.confirmed_junk, q?.audited),
      label: "Added to the skip list",
      sub: "of pages audited, the share confirmed as junk and never retried",
    },
    {
      icon: ScanSearch,
      value: pct(q?.llm_junk, q?.llm_judged),
      label: "Junk caught on deep check",
      sub: "of the borderline pages sent for a second, closer read",
    },
    {
      icon: Percent,
      value: q?.audited ? "Every page" : "—",
      label: "Checked before it is kept",
      sub: "quality is decided on the way in, not on the way out",
    },
  ];

  return (
    <div>
      {/* The dash is applied HERE rather than inside the tile, so a failed fetch sizes as the dash
          it actually renders instead of as the figure it could not get. */}
      <StatTileGrid
        items={stats.map((st) => ({ ...st, value: failed ? "—" : st.value }))}
      />
      <p className="mt-4 text-xs leading-relaxed text-foreground/45">
        Read live from the running system. Shown as rates rather than totals — the index is still
        growing, and a rate tells you how it is built rather than how far along it is.
      </p>
    </div>
  );
}
